import { Fragment, useState } from "react"
import DIYTandaSelected from "./diy_tanda_selected"
import { OpenAIResult, OpenAIStepResult, User } from "./utils/interfaces"
import { captureException } from "@sentry/react"
import parse from "html-react-parser"
import classnames from "classnames"
import proBadge from "images/plan-badges/pro.svg?raw"
import proPlusBadge from "images/plan-badges/pro_plus.svg?raw"
import Qs from "qs"

import { createAIMakerApplet } from "./api"

import styles from "./initial_step.module.scss"

import {
  trackAppletCreationCompleted,
  trackAppletCreationFailed,
} from "app/utils/mixpanel_tracking"

interface tandaSelectedProps {
  label: "if" | "with" | "then" | "and"
  separator: boolean
  showProFeatures: boolean
  step: OpenAIStepResult
}

const tandaSelected = ({ label, separator, showProFeatures, step }: tandaSelectedProps) => {
  return (
    <Fragment key={step.full_module_name}>
      {separator && <div className={styles.separator} />}
      <DIYTandaSelected
        readOnly={true}
        label={label}
        stepName={step.name}
        service={{
          id: "",
          image: step.channel.monochrome_image_url,
          name: step.full_module_name,
          brand_color: `#${step.channel.brand_color}`,
          allow_multiple_live_channels: false,
          live_channels: [],
        }}
        showProFeatures={showProFeatures}
      />
    </Fragment>
  )
}
interface Props {
  activationPath: string
  appletName: string
  previewState: OpenAIResult
  showProFeatures: boolean
  showUpgradeModal: (step: string, tier: string) => void
  tryAgainHandler: () => void
  user: User
  userAtQuota: boolean
}

const AutomateWithAIPreview = ({
  activationPath,
  appletName,
  previewState,
  showProFeatures,
  tryAgainHandler,
  showUpgradeModal,
  user,
  userAtQuota,
}: Props) => {
  const [loading, setLoading] = useState(false)
  const { block_user, block_reason, minimum_tier } = previewState.enablement_rules || {}
  const needUpgrade: boolean = block_user && user.tier !== minimum_tier

  const onSubmit = () => {
    if (userAtQuota) {
      showUpgradeModal("applet_quota")
    } else if (needUpgrade) {
      showUpgradeModal(block_reason, minimum_tier)
    } else {
      setLoading(true)

      createAIMakerApplet({
        appletName,
        triggerModuleNames: previewState.selectedTrigger.full_module_name,
        queriesModuleName: previewState.selectedQueries.map(query => query.full_module_name),
        actionsModuleName: previewState.selectedActions.map(action => action.full_module_name),
      })
        .then(result => {
          const normalizedApplet = result.diyAppletAIMakerCreate?.normalized_applet
          if (!normalizedApplet?.id) {
            setLoading(false)
            captureException(new Error("AI maker create returned missing normalized_applet"))
            window.appendFlash("Error creating your Applet", "danger")
            trackAppletCreationFailed({
              creationMode: "ai",
              entrypoint: "diy_composer",
              errorType: "missing_normalized_applet",
            })
            return
          }

          trackAppletCreationCompleted({
            creationMode: "ai",
            entrypoint: "diy_composer",
            appletId: normalizedApplet.id,
          })

          const returnTo = `${window.location.origin}/applets/${normalizedApplet.id}/edit`

          const servicesToConnect = (normalizedApplet.permissions ?? []).filter(
            permission => !permission.channel.connected
          )

            if (servicesToConnect.length == 0) {
              localStorage.setItem("ai_applet_being_created", normalizedApplet.id)
              window.location.assign(returnTo)
            } else {
              // Connect services one at a time - the edit page will loop through remaining services
              const firstService = servicesToConnect[0].channel.module_name

              window.location.assign(
                `${activationPath}?${Qs.stringify(
                  { c: [firstService], return_to: returnTo },
                  { arrayFormat: "brackets" }
                )}`
              )
            }
        })
        .catch(err => {
          setLoading(false)
          captureException(err)
          window.appendFlash("Error creating your Applet", "danger")

          trackAppletCreationFailed({
            creationMode: "ai",
            entrypoint: "diy_composer",
            errorType: err instanceof Error ? err.message : "ai_applet_create_failed",
          })
        })
    }
  }

  return (
    <>
      <section className={styles["diy-block"]}>
        {tandaSelected({ label: "if", separator: false, showProFeatures, step: previewState?.selectedTrigger })}
        {previewState?.selectedQueries.map(query =>
          tandaSelected({ label: "with", separator: true, showProFeatures, step: query })
        )}
        {previewState?.selectedActions.map((action, index) =>
          tandaSelected({ label: index == 0 ? "then" : "and", separator: true, showProFeatures, step: action })
        )}
      </section>
      <div className={styles.footer}>
        <section className="action-buttons-container">
          <button className="button-secondary" onClick={onSubmit} disabled={loading}>
            {needUpgrade ? (
              <span className={styles["upgrade-copy"]}>
                <span>Upgrade to</span>
                <span className={styles.badge}>{parse(minimum_tier == "pro" ? proPlusBadge : proBadge)}</span>
              </span>
            ) : (
              "Continue"
            )}
          </button>
          <button className={classnames("button-tertiary", "button-cancel-link", styles.retry)} onClick={tryAgainHandler}>
            Try again
          </button>
        </section>
      </div>
    </>
  )
}

export default AutomateWithAIPreview
